import { memo, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { ListItemButton, ListItemIcon } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setActiveSmallScreenMenu, openDrawer } from "../../../../../../store/reducers/menu";
import { CustomTooltip } from "../../../../../../components/custom-tooltip/CustomTooltip";
import { MinusOutlined } from "@ant-design/icons";

const NavItem = ({ item, level, setSubChildMenu }) => {
  const dispatch = useDispatch();

  // Memoized selector
  const { drawerOpen, openItem, activeSubMenu } = useSelector(state => state.menu);
  // Memoized values
  const isSelected = useMemo(() => openItem[0] === item.id, [openItem, item.id]);
  const isChildSelected = useMemo(() => openItem[1] === item.id, [openItem, item.id]);

  // Memoized styles
  const listItemStyles = useMemo(() => ({
    // zIndex: 1201,
    margin: '0px 7px 14px 7px',
    borderRadius: '7px',
    justifyContent: 'start',
    px: 0,
    py: !drawerOpen && level === 1 ? 1 : 1,
    '&.Mui-selected': {
      bgcolor: isSelected ? '#1890ff1f' : 'transparent',
      opacity: isSelected ? '100%' : '20%',
      ':hover': {
        backgroundColor: '#1890ff1f',
      },
    },
  }), [drawerOpen, level, isSelected]);

  // Event handlers
  const handleItemClick = useCallback((clickedItem) => {
    if (clickedItem?.subChildren?.length) {
      setSubChildMenu(clickedItem.subChildren);
      dispatch(openDrawer({ drawerOpen: true }));
      dispatch(setActiveSmallScreenMenu({ activeSmallScreenMenu: [clickedItem.id] }));
    } else {
      setSubChildMenu([]);
      dispatch(setActiveSmallScreenMenu([]));
      dispatch(openDrawer({ drawerOpen: false }));
    }
  }, [dispatch, setSubChildMenu]);

  const handleDrawerClose = useCallback(() => {
    dispatch(openDrawer({ drawerOpen: false }));
  }, [dispatch]);

  // Memoized components
  const Icon = item.icon;
  const itemIcon = useMemo(() => (
    item.icon ? (
      <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} />
    ) : null
  ), [item.icon, drawerOpen]);

  const ChildItem = () => (
    <div
      className={`flex flex-col hover:opacity-100 ${isChildSelected ? 'opacity-100' : 'opacity-50'
        }`}
    >
      <Link
        to={item?.subChildren ? undefined : item.url}
        state={{
          prescreen_dashboard: 0,
          feasibility_dashboard: 0,
          lsmDispatched_dashboard: 0,
          lsmAccepted_dashboard: 0,
          productManufacturing_dashboard: 0,
          finishedProductDispatch_dashboard: 0,
          infusion_dashboard: 0,
        }}
        onClick={handleDrawerClose}
        className="hover:no-underline text-white flex items-center gap-2"
      >
        <MinusOutlined />
        {item.title}
      </Link>
    </div>
  );

  const ParentItem = () => (
    <div>
      <ListItemButton
        component={item?.subChildren ? 'div' : Link}
        to={item?.subChildren ? undefined : item.url}
        onClick={() => handleItemClick(item)}
        selected={isSelected}
        className="opacity-50 hover:opacity-100 hover:bg-[#1890ff1f]"
        sx={listItemStyles}
      >
        <CustomTooltip title={item.title}>
          <ListItemIcon className="flex justify-center items-center py-1">
            {activeSubMenu !== item.id && itemIcon}
          </ListItemIcon>
        </CustomTooltip>
      </ListItemButton>
    </div>
  );

  return item.type === 'child-item' ? <ChildItem /> : <ParentItem />;
};

NavItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.string,
    url: PropTypes.string,
    icon: PropTypes.elementType,
    subChildren: PropTypes.array,
  }).isRequired,
  level: PropTypes.number.isRequired,
  setSubChildMenu: PropTypes.func,
};

export default memo(NavItem);
